/**
 * Iter 67da — compact Fleet-health summary chip for the Admin Operations
 * overview (above the tabs).
 *
 * Shows the platform-wide YTD net + bucket counts + breach count in a
 * single horizontal strip so admins get an at-a-glance health gauge
 * without clicking into the Fleet tab. Tapping anywhere on the strip
 * scrolls/switches to the full Fleet panel for the drill-down.
 *
 * Reads `/api/admin/fleet-health-summary` (same payload as the existing
 * `AdminFleetHealthChip`). Hides silently if the request fails —
 * non-fleet admins simply don't see the strip.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Money from "@/components/Money";
import {
  Activity, CircleCheck, CircleMinus, CircleX, Moon, AlertTriangle, ArrowRight,
} from "lucide-react";

export default function AdminFleetHealthSummaryStrip({ onOpenFleetTab }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/fleet-health-summary")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="rounded-2xl border border-border bg-neutral-50 px-4 py-3 text-xs text-neutral-400 animate-pulse mb-5" data-testid="admin-fleet-health-strip-loading">
        Loading platform fleet health…
      </div>
    );
  }

  const totals = data.totals || {};
  const buckets = data.buckets || {};
  const alerts = data.alerts || [];
  const net = Number(totals.net || 0);
  const netTone = net > 0
    ? "text-emerald-700 bg-emerald-50 ring-emerald-200"
    : net < 0
    ? "text-rose-700 bg-rose-50 ring-rose-200"
    : "text-neutral-700 bg-neutral-100 ring-neutral-200";

  return (
    <button
      type="button"
      onClick={onOpenFleetTab}
      className="w-full rounded-2xl bg-gradient-to-br from-white via-teal-50/30 to-rose-50/20 border border-emerald-100 hover:border-emerald-300 hover:shadow-md transition px-4 py-3 mb-5 text-left"
      data-testid="admin-fleet-health-strip"
      title="Open Fleet tab for the full drill-down"
    >
      <div className="flex items-center gap-3 flex-wrap">
        {/* Icon */}
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white flex items-center justify-center shadow-md shadow-teal-200 shrink-0">
          <Activity className="h-4 w-4" />
        </div>

        {/* Title */}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-teal-700">Fleet health · {data.year}</div>
          <div className="text-sm font-semibold text-neutral-900">
            YTD across {totals.fleet_size || 0} car{totals.fleet_size === 1 ? "" : "s"}
          </div>
        </div>

        {/* Net pill */}
        <div className={`inline-flex items-center gap-1.5 rounded-full ring-1 px-3 py-1 text-sm font-bold tabular-nums ${netTone}`} data-testid="admin-fleet-strip-net">
          Net <Money amount={net} />
        </div>

        {/* Bucket micro-chips */}
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-neutral-700">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 px-2 py-0.5" data-testid="admin-fleet-strip-positive">
            <CircleCheck className="h-3 w-3" /> {buckets.positive || 0}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-100 px-2 py-0.5" data-testid="admin-fleet-strip-breakeven">
            <CircleMinus className="h-3 w-3" /> {buckets.breakeven || 0}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-100 px-2 py-0.5" data-testid="admin-fleet-strip-negative">
            <CircleX className="h-3 w-3" /> {buckets.negative || 0}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 px-2 py-0.5" data-testid="admin-fleet-strip-dormant">
            <Moon className="h-3 w-3" /> {buckets.dormant || 0}
          </span>
        </div>

        {/* Breach count if any */}
        {alerts.length > 0 && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-200 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider" data-testid="admin-fleet-strip-alerts">
            <AlertTriangle className="h-3 w-3" /> {alerts.length} breach{alerts.length === 1 ? "" : "es"}
          </div>
        )}

        {/* Chevron */}
        <div className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-teal-700">
          Open Fleet <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </button>
  );
}
